/**
 * Ant Design Theme Configuration
 * Applies design tokens to Ant Design's theme system
 */

import { colors, spacing, typography, borderRadius, shadows, transitions } from './designTokens';

/**
 * Ant Design v5 Theme Config
 * @see https://ant.design/docs/react/customize-theme
 */
export const antdTheme = {
  // Token override for all components
  token: {
    // Colors
    colorPrimary: colors.primary[500],
    colorSuccess: colors.status.passLight,
    colorWarning: colors.status.warningLight,
    colorError: colors.status.failLight,
    colorInfo: colors.status.info,
    colorTextBase: colors.text.primary,
    colorBgBase: colors.background.default,

    // Border
    colorBorder: colors.border.light,
    colorBorderSecondary: colors.border.main,
    borderRadius: borderRadius.sm,
    borderRadiusLG: borderRadius.md,
    borderRadiusSM: borderRadius.sm,

    // Font
    fontFamily: typography.fontFamily.base,
    fontSize: typography.fontSize.base,
    fontSizeHeading1: typography.headings.h1.fontSize,
    fontSizeHeading2: typography.headings.h2.fontSize,
    fontSizeHeading3: typography.headings.h3.fontSize,
    fontSizeHeading4: typography.headings.h4.fontSize,
    fontSizeHeading5: typography.headings.h5.fontSize,
    fontWeightStrong: typography.fontWeight.semibold,

    // Spacing
    marginXS: spacing.xs,
    marginSM: spacing.sm,
    margin: spacing.md,
    marginMD: spacing.md,
    marginLG: spacing.lg,
    marginXL: spacing.xl,
    paddingXS: spacing.xs,
    paddingSM: spacing.sm,
    padding: spacing.md,
    paddingMD: spacing.md,
    paddingLG: spacing.lg,
    paddingXL: spacing.xl,

    // Control sizes
    controlHeight: 32,
    controlHeightSM: 24,
    controlHeightLG: 40,

    // Line height
    lineHeight: typography.lineHeight.normal,
    lineHeightHeading: typography.lineHeight.tight,

    // Shadows
    boxShadow: shadows.sm,
    boxShadowSecondary: shadows.md,

    // Motion
    motionDurationFast: transitions.duration.fast,
    motionDurationMid: transitions.duration.base,
    motionDurationSlow: transitions.duration.slow,
    motionEaseInOut: transitions.easing.easeInOut,
    motionEaseOut: transitions.easing.easeOut,
  },

  // Component-specific overrides
  components: {
    // Button (applies to both standard and icon buttons)
    Button: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      borderRadius: borderRadius.sm,
      fontWeight: typography.fontWeight.medium,
      // Primary button colors (standard & icon)
      colorPrimary: '#2B1A42',          // Still Truvex, but readable/breathable
      colorPrimaryHover: '#4B3781',     // Slight lift; smooth transitions
      colorPrimaryActive: '#241539',    // Press state (slightly darker)
      colorPrimaryBorder: '#2B1A42',
      // Disabled state
      colorBgContainerDisabled: '#AFA8B7',  // Neutral desaturated version
      colorTextDisabled: '#ffffff',          // White text on disabled
    },

    // Table
    Table: {
      borderRadius: borderRadius.md,
      headerBg: colors.neutral[50],
      headerColor: '#3D3D3D',               // Softer header text (not pure black)
      headerFontWeight: 600,                // Semi-bold headers
      rowHoverBg: '#FAF9FB',                // Subtle tint, not gray
      rowSelectedBg: '#F7F5FC',             // Light lavender for selected rows
      rowSelectedHoverBg: '#F4F1FA',        // Slightly darker on hover
      fontSize: typography.fontSize.sm,
      cellPaddingBlock: spacing.sm,
      cellPaddingInline: spacing.md,
      colorBorderSecondary: 'rgba(0, 0, 0, 0.06)', // Lighter, airier borders
    },

    // Card
    Card: {
      borderRadius: borderRadius.md,
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.04)', // Very soft shadow
      paddingLG: spacing.lg,
      colorBorderSecondary: colors.border.light, // Warmer border #EBE8F0
    },

    // Modal
    Modal: {
      borderRadius: borderRadius.lg,
      paddingContentHorizontal: spacing.lg,
      paddingMD: spacing.lg,
      // Close icon styling
      colorIcon: '#888888',
      colorIconHover: '#555555',
    },

    // Tag
    Tag: {
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.xs,
      fontSizeSM: typography.fontSize.xs,
    },

    // Input
    Input: {
      borderRadius: borderRadius.sm,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      paddingBlock: spacing.xs,
      paddingInline: spacing.sm,
    },

    // Select
    Select: {
      borderRadius: borderRadius.sm,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      controlOutline: 'rgba(0,0,0,0)',
      controlOutlineWidth: 0,
      activeShadow: 'none',
      boxShadow: 'none',
      boxShadowSecondary: 'none',
      colorBorder: '#E4E1E8',
      colorPrimaryBorder: '#4B3781',
      colorPrimaryHover: '#4B3781',
      controlItemBgActive: '#F4F1FA', // Lavender bg on selected item
      controlItemBgHover: '#F7F5FC', // Light hover in dropdown
      optionSelectedBg: '#F4F1FA', // Selected option in dropdown
      selectorBg: '#FFFFFF', // Selector background
      optionActiveBg: '#F4F1FA', // Active option bg
      showArrow: true, // Always show dropdown arrow
      colorIcon: '#6A608A', // Darker arrow color
      colorIconHover: '#4B3781', // Purple on hover
    },

    // Tabs
    Tabs: {
      horizontalMargin: `0 0 ${spacing.lg}px 0`,
      horizontalItemPadding: `${spacing.sm}px 0`,
      horizontalItemGutter: spacing.xl,
    },

    // Alert
    Alert: {
      borderRadius: borderRadius.md,
      paddingContentVertical: spacing.sm,
      paddingContentHorizontal: spacing.md,
    },

    // Collapse
    Collapse: {
      borderRadius: borderRadius.md,
      headerPadding: `12px ${spacing.lg}px`,       // More vertical padding (compact mode)
      contentPadding: `${spacing.md}px ${spacing.lg}px`,
      fontWeightStrong: typography.fontWeight.semibold, // Bold heading for toggle cue
    },

    // Drawer
    Drawer: {
      paddingLG: spacing.lg,
    },

    // Tooltip
    Tooltip: {
      borderRadius: borderRadius.sm,
    },

    // Badge
    Badge: {
      fontSize: typography.fontSize.xs,
      fontSizeSM: typography.fontSize.xs,
    },

    // Progress
    Progress: {
      defaultColor: colors.primary[500],
      remainingColor: colors.neutral[200],
      circleTextColor: colors.text.primary,
      // Smooth animations (800ms ease-out)
      motionDurationSlow: '800ms',
    },

    // Spin
    Spin: {
      colorPrimary: colors.primary[500],
    },

    // Layout (for sidebar)
    // Note: Two-panel navigation uses:
    //   - Primary sidebar (global nav): colors.sidebar.primary (#17052B)
    //   - Secondary sidebar (contextual nav): colors.sidebar.secondary (#241133)
    Layout: {
      headerBg: colors.neutral[0],
      headerPadding: `0 ${spacing.lg}px`,
      headerHeight: 64,
      siderBg: colors.sidebar.primary, // Primary sidebar default
      bodyBg: colors.background.default,
    },

    // Menu (for sidebar navigation)
    Menu: {
      itemBg: 'transparent',
      itemColor: 'rgba(255, 255, 255, 0.75)', // Softer white
      itemHoverColor: colors.neutral[0],
      itemHoverBg: 'rgba(255, 255, 255, 0.08)', // Subtle hover
      itemSelectedBg: 'rgba(255, 255, 255, 0.15)', // Selected state
      itemSelectedColor: colors.neutral[0],
      itemBorderRadius: borderRadius.sm,
      itemMarginBlock: 4,
      itemPaddingInline: spacing.md,
      groupTitleColor: 'rgba(255, 255, 255, 0.5)', // Group labels
    },

    // Breadcrumb
    Breadcrumb: {
      fontSize: typography.fontSize.sm,
      separatorMargin: spacing.xs,
    },

    // Divider
    Divider: {
      colorSplit: colors.border.main, // Cleaner neutral tone (#EBEBEB)
    },

    // Form
    Form: {
      labelFontSize: typography.fontSize.sm,
      itemMarginBottom: spacing.lg,
      verticalLabelPadding: `0 0 ${spacing.xs}px`,
    },
  },

  // Algorithm (for dark mode support in future)
  // algorithm: theme.darkAlgorithm, // Uncomment for dark mode
};

export default antdTheme;
